import { supabase } from './supabaseClient';
import { User } from '@supabase/supabase-js';

// Database entity types
export interface DbProfile {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
  country: string | null;
  interests: string[] | null;
  preferences: Record<string, any> | null;
  last_viewed_card_id: string | null;
  last_topic_filter: string | null;
  onboarding_completed: boolean;
  created_at: string;
  updated_at: string;
}

export interface DbPlaylistSong {
  id: string;
  playlist_id: string;
  song_id: string;
  sort_order: number;
  created_at: string;
}

export interface DbUserSessionState {
  id: string;
  user_id: string;
  state_data: Record<string, any>;
  updated_at: string;
}

export interface DbSong {
  id: string;
  title: string;
  artist: string;
  slug: string;
  description: string | null;
  lyric_spotlight: string | null;
  story_body: string | null;
  timeline_json: any | null;
  context_in_time: string | null;
  deep_insight: string | null;
  quick_fact: string | null;
  related_song_ids: string[] | null;
  image_url: string | null;
  spotify_url: string | null;
  apple_music_url: string | null;
  youtube_url: string | null;
  tags: string[] | null;
  themes: string[] | null;
  status: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  published_at: string | null;
}

export interface DbIssue {
  id: string;
  slug: string;
  label: string;
  description: string | null;
  color: string | null;
}

export interface DbSongWithIssues extends DbSong {
  issues: DbIssue[];
}

export interface DbPlaylist {
  id: string;
  name: string;
  slug: string | null;
  description: string | null;
  is_curated: boolean;
  created_by: string | null;
  created_at: string;
}

export interface DbUserSavedSong {
  id: string;
  user_id: string;
  song_id: string;
  saved_at: string;
  song: DbSong;
}

export interface DbUserSavedPlaylist {
  id: string;
  user_id: string;
  playlist_id: string;
  saved_at: string;
  playlist: DbPlaylist;
}

export interface DbUserEvent {
  id: string;
  user_id: string;
  song_id: string | null;
  playlist_id: string | null;
  event_type: string;
  payload: any;
  created_at: string;
}

export interface DbSongSubmission {
  id: string;
  user_id: string | null;
  song_title: string;
  artist_name: string;
  issue_tags: string[];
  context_text: string | null;
  status: string;
  created_at: string;
  reviewed_at: string | null;
}

// Auth functions
export async function getCurrentUser(): Promise<User | null> {
  try {
    const { data, error } = await supabase.auth.getUser();

    if (error) {
      console.error('Error getting current user:', error.message);
      return null;
    }

    return data.user;
  } catch (err) {
    console.error('Unexpected error getting current user:', err);
    return null;
  }
}

export async function signUp(email: string, password: string): Promise<User | null> {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });

    if (error) {
      console.error('Error signing up:', error.message);
      return null;
    }

    return data.user;
  } catch (err) {
    console.error('Unexpected error signing up:', err);
    return null;
  }
}

export async function signIn(email: string, password: string): Promise<User | null> {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      console.error('Error signing in:', error.message);
      return null;
    }

    return data.user;
  } catch (err) {
    console.error('Unexpected error signing in:', err);
    return null;
  }
}

export async function signOut(): Promise<boolean> {
  try {
    const { error } = await supabase.auth.signOut();

    if (error) {
      console.error('Error signing out:', error.message);
      return false;
    }

    return true;
  } catch (err) {
    console.error('Unexpected error signing out:', err);
    return false;
  }
}

// Profile functions
export async function getProfile(userId: string): Promise<DbProfile | null> {
  try {
    // Fetch profile and interests in parallel
    const [profileResult, interestsResult] = await Promise.all([
      supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle(),
      supabase
        .from('user_interests')
        .select('issue_tag')
        .eq('user_id', userId),
    ]);

    if (profileResult.error) {
      console.error('Error fetching profile:', profileResult.error.message);
      return null;
    }

    if (!profileResult.data) {
      return null;
    }

    // Map interests from junction table to array
    const interests = interestsResult.data?.map(i => i.issue_tag) || [];

    return {
      ...profileResult.data,
      interests,
    };
  } catch (err) {
    console.error('Unexpected error fetching profile:', err);
    return null;
  }
}

export async function upsertProfile(profile: {
  id: string;
  display_name?: string;
  avatar_url?: string;
  country?: string;
  interests?: string[];
}): Promise<DbProfile | null> {
  try {
    const updateData: any = {
      id: profile.id,
      updated_at: new Date().toISOString(),
    };

    if (profile.display_name !== undefined) updateData.display_name = profile.display_name;
    if (profile.avatar_url !== undefined) updateData.avatar_url = profile.avatar_url;
    if (profile.country !== undefined) updateData.country = profile.country;

    // Update profile (without interests field)
    const { data, error } = await supabase
      .from('profiles')
      .upsert(updateData)
      .select()
      .single();

    if (error) {
      console.error('Error upserting profile:', error.message);
      return null;
    }

    // If interests were provided, update user_interests table separately
    if (profile.interests !== undefined) {
      // Delete existing interests
      await supabase
        .from('user_interests')
        .delete()
        .eq('user_id', profile.id);

      // Insert new interests
      if (profile.interests.length > 0) {
        const interestsData = profile.interests.map(tag => ({
          user_id: profile.id,
          issue_tag: tag,
        }));

        const { error: interestsError } = await supabase
          .from('user_interests')
          .insert(interestsData);

        if (interestsError) {
          console.error('Error updating interests:', interestsError.message);
        }
      }
    }

    // Return profile with interests
    return getProfile(profile.id);
  } catch (err) {
    console.error('Unexpected error upserting profile:', err);
    return null;
  }
}

// Song functions
export async function getActiveSongsWithIssues(filters?: {
  issueSlug?: string;
}): Promise<DbSongWithIssues[]> {
  try {
    let query = supabase
      .from('songs')
      .select(`
        *,
        song_issues!inner (
          issue:issues (*)
        )
      `)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (filters?.issueSlug) {
      query = query.eq('song_issues.issues.slug', filters.issueSlug);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching songs with issues:', error.message);
      return [];
    }

    if (!data) {
      return [];
    }

    // Transform the nested structure to flat issues array
    const songsWithIssues: DbSongWithIssues[] = data.map((song: any) => {
      const issues = song.song_issues?.map((si: any) => si.issue).filter(Boolean) || [];
      const { song_issues, ...songData } = song;
      return {
        ...songData,
        issues,
      };
    });

    return songsWithIssues;
  } catch (err) {
    console.error('Unexpected error fetching songs with issues:', err);
    return [];
  }
}

export async function getSongBySlug(slug: string): Promise<DbSongWithIssues | null> {
  try {
    const { data, error } = await supabase
      .from('songs')
      .select(`
        *,
        song_issues (
          issue:issues (*)
        )
      `)
      .eq('slug', slug)
      .eq('is_active', true)
      .maybeSingle();

    if (error) {
      console.error('Error fetching song by slug:', error.message);
      return null;
    }

    if (!data) {
      return null;
    }

    // Transform the nested structure
    const issues = data.song_issues?.map((si: any) => si.issue).filter(Boolean) || [];
    const { song_issues, ...songData } = data;

    return {
      ...songData,
      issues,
    };
  } catch (err) {
    console.error('Unexpected error fetching song by slug:', err);
    return null;
  }
}

export async function getSongById(songId: string): Promise<DbSongWithIssues | null> {
  try {
    const { data, error } = await supabase
      .from('songs')
      .select(`
        *,
        song_issues (
          issue:issues (*)
        )
      `)
      .eq('id', songId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching song by id:', error.message);
      return null;
    }

    if (!data) {
      return null;
    }

    // Transform the nested structure
    const issues = data.song_issues?.map((si: any) => si.issue).filter(Boolean) || [];
    const { song_issues, ...songData } = data;

    return {
      ...songData,
      issues,
    };
  } catch (err) {
    console.error('Unexpected error fetching song by id:', err);
    return null;
  }
}

// User saved songs functions
export async function getUserSavedSongs(userId: string): Promise<DbUserSavedSong[]> {
  try {
    const { data, error } = await supabase
      .from('user_saved_songs')
      .select(`
        *,
        song:songs (*)
      `)
      .eq('user_id', userId)
      .order('saved_at', { ascending: false });

    if (error) {
      console.error('Error fetching user saved songs:', error.message);
      return [];
    }

    return data || [];
  } catch (err) {
    console.error('Unexpected error fetching user saved songs:', err);
    return [];
  }
}

export async function saveSongForUser(userId: string, songId: string): Promise<boolean> {
  try {
    // Insert into user_saved_songs
    const { error: saveError } = await supabase
      .from('user_saved_songs')
      .insert({
        user_id: userId,
        song_id: songId,
      });

    if (saveError) {
      console.error('Error saving song for user:', saveError.message);
      return false;
    }

    // Log the event
    await logUserEvent({
      userId,
      songId,
      eventType: 'SAVE_SONG',
      payload: { action: 'save' },
    });

    return true;
  } catch (err) {
    console.error('Unexpected error saving song for user:', err);
    return false;
  }
}

export async function unsaveSongForUser(userId: string, songId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('user_saved_songs')
      .delete()
      .eq('user_id', userId)
      .eq('song_id', songId);

    if (error) {
      console.error('Error unsaving song for user:', error.message);
      return false;
    }

    // Log the event
    await logUserEvent({
      userId,
      songId,
      eventType: 'UNSAVE_SONG',
      payload: { action: 'unsave' },
    });

    return true;
  } catch (err) {
    console.error('Unexpected error unsaving song for user:', err);
    return false;
  }
}

export async function isSongSavedByUser(userId: string, songId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('user_saved_songs')
      .select('id')
      .eq('user_id', userId)
      .eq('song_id', songId)
      .maybeSingle();

    if (error) {
      console.error('Error checking if song is saved:', error.message);
      return false;
    }

    return !!data;
  } catch (err) {
    console.error('Unexpected error checking if song is saved:', err);
    return false;
  }
}

// Playlist functions
export async function getUserSavedPlaylists(userId: string): Promise<DbUserSavedPlaylist[]> {
  try {
    const { data, error } = await supabase
      .from('user_saved_playlists')
      .select(`
        *,
        playlist:playlists (*)
      `)
      .eq('user_id', userId)
      .order('saved_at', { ascending: false });

    if (error) {
      console.error('Error fetching user saved playlists:', error.message);
      return [];
    }

    return data || [];
  } catch (err) {
    console.error('Unexpected error fetching user saved playlists:', err);
    return [];
  }
}

export async function savePlaylistForUser(userId: string, playlistId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('user_saved_playlists')
      .insert({
        user_id: userId,
        playlist_id: playlistId,
      });

    if (error) {
      console.error('Error saving playlist for user:', error.message);
      return false;
    }

    // Log the event
    await logUserEvent({
      userId,
      playlistId,
      eventType: 'SAVE_PLAYLIST',
      payload: { action: 'save' },
    });

    return true;
  } catch (err) {
    console.error('Unexpected error saving playlist for user:', err);
    return false;
  }
}

export async function unsavePlaylistForUser(userId: string, playlistId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('user_saved_playlists')
      .delete()
      .eq('user_id', userId)
      .eq('playlist_id', playlistId);

    if (error) {
      console.error('Error unsaving playlist for user:', error.message);
      return false;
    }

    // Log the event
    await logUserEvent({
      userId,
      playlistId,
      eventType: 'UNSAVE_PLAYLIST',
      payload: { action: 'unsave' },
    });

    return true;
  } catch (err) {
    console.error('Unexpected error unsaving playlist for user:', err);
    return false;
  }
}

export async function isPlaylistSavedByUser(userId: string, playlistId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('user_saved_playlists')
      .select('id')
      .eq('user_id', userId)
      .eq('playlist_id', playlistId)
      .maybeSingle();

    if (error) {
      console.error('Error checking if playlist is saved:', error.message);
      return false;
    }

    return !!data;
  } catch (err) {
    console.error('Unexpected error checking if playlist is saved:', err);
    return false;
  }
}

// User events functions
export async function logUserEvent(event: {
  userId: string;
  songId?: string;
  playlistId?: string;
  eventType: string;
  payload?: any;
}): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('user_events')
      .insert({
        user_id: event.userId,
        song_id: event.songId || null,
        playlist_id: event.playlistId || null,
        event_type: event.eventType,
        payload: event.payload || {},
      });

    if (error) {
      console.error('Error logging user event:', error.message);
      return false;
    }

    return true;
  } catch (err) {
    console.error('Unexpected error logging user event:', err);
    return false;
  }
}

export async function getUserEvents(userId: string, limit = 100): Promise<DbUserEvent[]> {
  try {
    const { data, error } = await supabase
      .from('user_events')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching user events:', error.message);
      return [];
    }

    return data || [];
  } catch (err) {
    console.error('Unexpected error fetching user events:', err);
    return [];
  }
}

// Issues functions
export async function getAllIssues(): Promise<DbIssue[]> {
  try {
    const { data, error } = await supabase
      .from('issues')
      .select('*')
      .order('label', { ascending: true });

    if (error) {
      console.error('Error fetching issues:', error.message);
      return [];
    }

    return data || [];
  } catch (err) {
    console.error('Unexpected error fetching issues:', err);
    return [];
  }
}

// Song submissions functions
export async function submitSong(submission: {
  userId?: string;
  songTitle: string;
  artistName: string;
  issueTags: string[];
  contextText?: string;
}): Promise<DbSongSubmission | null> {
  try {
    const { data, error } = await supabase
      .from('song_submissions')
      .insert({
        user_id: submission.userId || null,
        song_title: submission.songTitle,
        artist_name: submission.artistName,
        issue_tags: submission.issueTags,
        context_text: submission.contextText || null,
        status: 'pending',
      })
      .select()
      .single();

    if (error) {
      console.error('Error submitting song:', error.message);
      return null;
    }

    return data;
  } catch (err) {
    console.error('Unexpected error submitting song:', err);
    return null;
  }
}

export async function getUserSubmissions(userId: string): Promise<DbSongSubmission[]> {
  try {
    const { data, error } = await supabase
      .from('song_submissions')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching user submissions:', error.message);
      return [];
    }

    return data || [];
  } catch (err) {
    console.error('Unexpected error fetching user submissions:', err);
    return [];
  }
}

// Playlists with songs
export async function getPlaylistWithSongs(playlistId: string): Promise<(DbPlaylist & { songs: DbSong[] }) | null> {
  try {
    const { data: playlist, error: playlistError } = await supabase
      .from('playlists')
      .select('*')
      .eq('id', playlistId)
      .maybeSingle();

    if (playlistError || !playlist) {
      console.error('Error fetching playlist:', playlistError?.message);
      return null;
    }

    const { data: playlistSongs, error: songsError } = await supabase
      .from('playlist_songs')
      .select(`
        song:songs (*)
      `)
      .eq('playlist_id', playlistId)
      .order('sort_order', { ascending: true });

    if (songsError) {
      console.error('Error fetching playlist songs:', songsError.message);
      return null;
    }

    const songs = playlistSongs?.map((ps: any) => ps.song).filter(Boolean) || [];

    return {
      ...playlist,
      songs,
    };
  } catch (err) {
    console.error('Unexpected error fetching playlist with songs:', err);
    return null;
  }
}

export async function getAllPlaylists(): Promise<DbPlaylist[]> {
  try {
    const { data, error } = await supabase
      .from('playlists')
      .select('*')
      .order('created_at', { ascending: false});

    if (error) {
      console.error('Error fetching playlists:', error.message);
      return [];
    }

    return data || [];
  } catch (err) {
    console.error('Unexpected error fetching playlists:', err);
    return [];
  }
}

// Playlist songs functions
export async function getPlaylistSongs(playlistId: string): Promise<DbSong[]> {
  try {
    const { data, error } = await supabase
      .from('playlist_songs')
      .select(`
        song:songs (*)
      `)
      .eq('playlist_id', playlistId)
      .order('sort_order', { ascending: true });

    if (error) {
      console.error('Error fetching playlist songs:', error.message);
      return [];
    }

    return data?.map((ps: any) => ps.song).filter(Boolean) || [];
  } catch (err) {
    console.error('Unexpected error fetching playlist songs:', err);
    return [];
  }
}

// Session state functions
export async function getUserSessionState(userId: string): Promise<DbUserSessionState | null> {
  try {
    const { data, error } = await supabase
      .from('user_session_state')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching user session state:', error.message);
      return null;
    }

    return data;
  } catch (err) {
    console.error('Unexpected error fetching user session state:', err);
    return null;
  }
}

export async function upsertUserSessionState(
  userId: string,
  stateData: Record<string, any>
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('user_session_state')
      .upsert({
        user_id: userId,
        state_data: stateData,
        updated_at: new Date().toISOString(),
      });

    if (error) {
      console.error('Error upserting user session state:', error.message);
      return false;
    }

    return true;
  } catch (err) {
    console.error('Unexpected error upserting user session state:', err);
    return false;
  }
}

// Derived data functions - compute identity moments from user_events
export async function getDerivedIdentityMoments(userId: string): Promise<Array<{
  cardId: string;
  type: 'resonates' | 'worried' | 'curious';
  issueTags: string[];
  timestamp: number;
}>> {
  try {
    const { data, error } = await supabase
      .from('user_events')
      .select('*')
      .eq('user_id', userId)
      .eq('event_type', 'REACTION_MEANING')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching identity moments:', error.message);
      return [];
    }

    const moments = data?.map(event => ({
      cardId: event.song_id || '',
      type: event.payload?.meaning as 'resonates' | 'worried' | 'curious',
      issueTags: event.payload?.issueTags || [],
      timestamp: new Date(event.created_at).getTime(),
    })).filter(m => m.cardId && m.type) || [];

    return moments;
  } catch (err) {
    console.error('Unexpected error fetching identity moments:', err);
    return [];
  }
}

// Derived data functions - compute moods from user_events
export async function getDerivedMoodEntries(userId: string): Promise<Array<{
  cardId: string;
  moods: string[];
  timestamp: number;
  songTitle: string;
  artistName: string;
}>> {
  try {
    // Fetch from user_feelings table instead of user_events
    const { data: feelingsData, error: feelingsError } = await supabase
      .from('user_feelings')
      .select('story_id, feeling_type, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (feelingsError) {
      console.error('Error fetching mood entries:', feelingsError.message);
      return [];
    }

    // Also get song info for each story
    const storyIds = [...new Set(feelingsData?.map(f => f.story_id).filter(Boolean))];
    const { data: songsData } = await supabase
      .from('songs')
      .select('id, title, artist')
      .in('id', storyIds);

    const songMap = new Map(songsData?.map(s => [s.id, { title: s.title, artist: s.artist }]) || []);

    // Group by story_id and collect emotions
    const moodMap = new Map<string, {
      moods: Set<string>;
      timestamp: number;
      songTitle: string;
      artistName: string;
    }>();

    feelingsData?.forEach(feeling => {
      const cardId = feeling.story_id || '';
      const emotion = feeling.feeling_type;
      const songInfo = songMap.get(cardId);

      if (cardId && emotion) {
        if (!moodMap.has(cardId)) {
          moodMap.set(cardId, {
            moods: new Set(),
            timestamp: new Date(feeling.created_at).getTime(),
            songTitle: songInfo?.title || '',
            artistName: songInfo?.artist || '',
          });
        }
        const entry = moodMap.get(cardId)!;
        // Map feeling_type to CivicMood format (capitalize first letter)
        const moodName = emotion.charAt(0).toUpperCase() + emotion.slice(1);
        entry.moods.add(moodName);
        // Update timestamp to most recent
        const eventTime = new Date(feeling.created_at).getTime();
        if (eventTime > entry.timestamp) {
          entry.timestamp = eventTime;
        }
      }
    });

    return Array.from(moodMap.entries()).map(([cardId, data]) => ({
      cardId,
      moods: Array.from(data.moods),
      timestamp: data.timestamp,
      songTitle: data.songTitle,
      artistName: data.artistName,
    }));
  } catch (err) {
    console.error('Unexpected error fetching mood entries:', err);
    return [];
  }
}

// Get card view history from user_events
export async function getDerivedCardViewHistory(userId: string): Promise<Record<string, number>> {
  try {
    const { data, error } = await supabase
      .from('user_events')
      .select('song_id')
      .eq('user_id', userId)
      .eq('event_type', 'VIEW_STORY');

    if (error) {
      console.error('Error fetching card view history:', error.message);
      return {};
    }

    const history: Record<string, number> = {};
    data?.forEach(event => {
      const songId = event.song_id;
      if (songId) {
        history[songId] = (history[songId] || 0) + 1;
      }
    });

    return history;
  } catch (err) {
    console.error('Unexpected error fetching card view history:', err);
    return {};
  }
}

// Delete all user data from the database (for profile reset)
export async function deleteAllUserData(userId: string): Promise<{ success: boolean; error?: string }> {
  try {
    // Delete in order to avoid foreign key constraint issues
    // 1. Delete user events (all engagement history)
    const { error: eventsError } = await supabase
      .from('user_events')
      .delete()
      .eq('user_id', userId);

    if (eventsError) {
      console.error('Error deleting user events:', eventsError.message);
      return { success: false, error: eventsError.message };
    }

    // 2. Delete saved songs
    const { error: savedSongsError } = await supabase
      .from('user_saved_songs')
      .delete()
      .eq('user_id', userId);

    if (savedSongsError) {
      console.error('Error deleting saved songs:', savedSongsError.message);
      return { success: false, error: savedSongsError.message };
    }

    // 3. Delete saved playlists
    const { error: savedPlaylistsError } = await supabase
      .from('user_saved_playlists')
      .delete()
      .eq('user_id', userId);

    if (savedPlaylistsError) {
      console.error('Error deleting saved playlists:', savedPlaylistsError.message);
      return { success: false, error: savedPlaylistsError.message };
    }

    // 4. Delete session state
    const { error: sessionError } = await supabase
      .from('user_session_state')
      .delete()
      .eq('user_id', userId);

    if (sessionError) {
      console.error('Error deleting session state:', sessionError.message);
      return { success: false, error: sessionError.message };
    }

    // 5. Delete user interests from junction table
    const { error: interestsError } = await supabase
      .from('user_interests')
      .delete()
      .eq('user_id', userId);

    if (interestsError) {
      console.error('Error deleting interests:', interestsError.message);
      return { success: false, error: interestsError.message };
    }

    // 6. Reset profile personalization data (keep the profile but clear preferences)
    const { data: updatedProfile, error: profileError } = await supabase
      .from('profiles')
      .update({
        last_viewed_card_id: null,
        last_topic_filter: null,
        preferences: null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId)
      .select();

    if (profileError) {
      console.error('Error resetting profile:', profileError.message);
      return { success: false, error: profileError.message };
    }

    console.log('[deleteAllUserData] Profile reset successful:', updatedProfile);

    return { success: true };
  } catch (err) {
    console.error('Unexpected error deleting user data:', err);
    return { success: false, error: 'An unexpected error occurred' };
  }
}
